import { effect, Injectable, signal } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { Observable } from 'rxjs';

const HideEmptyMIKey = 'hideEmptyMI';
@Injectable()
export class DataSharingService {
  private dataSourceTagToDelete = new BehaviorSubject<any>('');
  currentSelectedTagIdToDelete = this.dataSourceTagToDelete.asObservable();

  private dataSourceSelectedCurrentCompanyIds = new BehaviorSubject<any>([]);
  currentSelectedCompanyIds =
    this.dataSourceSelectedCurrentCompanyIds.asObservable();

  private dataSourceSelectedCurrentSearchParams = new BehaviorSubject<any>(
    new HttpParams()
  );
  currentSelectedSearchParams =
    this.dataSourceSelectedCurrentSearchParams.asObservable();

  private magicInsightsUnlockCountSource = new BehaviorSubject<number>(null);
  currentMagicInsightsUnlockCount =
    this.magicInsightsUnlockCountSource.asObservable();

  private zgResultCountSource = new BehaviorSubject<number>(0);
  currentZgResultCount: Observable<number> =
    this.zgResultCountSource.asObservable();

  companyTagsUpdated = signal(false);
  companyNumberFromParent = signal('');
  unlockMagicModuleId = signal(null);
  hideEmptyInsights = signal(this.getExistingHideEmptyState() || false);

  /**
   * profile data related caches
   */
  cachedCorporateStructureMap = new Map<string, Observable<any>>();
  cachedImmediateClustersMap = new Map<string, Observable<any>>();
  cachedFullClustersMap = new Map<string, Observable<any>>();
  cachedSupplierDataMap = new Map<string, Observable<any>>();
  cachedDetailedImportsExportsDataMap = new Map<string, Observable<any>>();
  cachedCrossSellDataMap = new Map<string, Observable<any>>();
  cachedAIRADataMap = new Map<string, Observable<any>>();

  //-- caches above should also be listed here for clearing --
  private cacheMaps: Map<string, Observable<any>>[] = [
    this.cachedCorporateStructureMap,
    this.cachedImmediateClustersMap,
    this.cachedFullClustersMap,
    this.cachedSupplierDataMap,
    this.cachedDetailedImportsExportsDataMap,
    this.cachedCrossSellDataMap,
    this.cachedAIRADataMap,
  ];
  // --------------- //

  constructor() {
    effect(() => {
      const currentHideMIState = this.hideEmptyInsights();
      try {
        localStorage.setItem(
          HideEmptyMIKey,
          JSON.stringify(currentHideMIState)
        );
      } catch (error) {
        console.warn('local storage error', error);
      }
    });
    //listen to storage changes to reflect across the app
    window.addEventListener('storage', (storageEvent: StorageEvent) =>
      this.handleLocalStorageChange(storageEvent)
    );
  }

  changeDataSourceCurrentCompanyIds(inputData: any) {
    this.dataSourceSelectedCurrentCompanyIds.next(inputData);
    // We don't actually want to clear these HTTP Params as the query is required for downloading to add ES Colums
    // this.dataSourceSelectedCurrentSearchParams.next(new HttpParams());
    this.dataSourceTagToDelete.next('');
  }

  updateJustCurrentSearchParams(searchParams: any) {
    this.dataSourceSelectedCurrentSearchParams.next(searchParams);
  }

  changeDataSourceCurrentSearchParams(searchParams: any) {
    this.dataSourceSelectedCurrentSearchParams.next(searchParams);
    this.dataSourceSelectedCurrentCompanyIds.next([]);
    this.dataSourceTagToDelete.next('');
  }

  changeDataSourceTagIdToDelete(tagIdToDelete: any) {
    this.dataSourceTagToDelete.next(tagIdToDelete);
  }

  setMagicInsightsUnlockCount(count: number | null): void {
    this.magicInsightsUnlockCountSource.next(count);
  }

  clearTagToDelete() {
    this.dataSourceTagToDelete.next('');
  }

  updateZgResultCount(count: number) {
    this.zgResultCountSource.next(count);
  }

  clearProfileDataCaches(): void {
    this.cacheMaps.forEach(map => map.clear());
  }

  getExistingHideEmptyState(): boolean {
    try {
      const existingState = JSON.parse(localStorage.getItem(HideEmptyMIKey));
      return !!existingState;
    } catch (error) {
      console.warn('error retrieving the state', error);
    }
  }

  updateHideEmptyMIInsights(): void {
    this.hideEmptyInsights.update(currentState => !currentState);
  }

  handleLocalStorageChange(event: StorageEvent): void {
    if (event.key === HideEmptyMIKey && event.newValue !== null) {
      const updatedValue = JSON.parse(event.newValue);

      this.hideEmptyInsights.set(updatedValue);
    }
  }

  ngOnDestroy() {
    window.removeEventListener('storage', (storageEvent: StorageEvent) =>
      this.handleLocalStorageChange(storageEvent)
    );
  }
}
